import { useEffect, useMemo, useRef, useState } from "react";
import { Textarea } from "@/components/ui/textarea";
import { Linkify } from "@/components/shared/Linkify";
import { Avatar } from "@/components/shared/Avatar";

// Označování lidí v komentářích (172). Napíšeš „@", vyskočí seznam lidí, výběrem
// se vloží „@Jméno Příjmení". Id označených se posílají zvlášť (`mentions`), text
// zůstává čitelný i bez aplikace (e-mail, export).

const ROLE = { OWNER: "Majitel", MANAGER: "Stavbyvedoucí", ASSISTANT: "Asistent/ka", WORKER: "Dělník",
  SUBCONTRACTOR: "Subdodavatel", ACCOUNTANT: "Účetní", WAREHOUSE: "Skladník", DESIGNER: "Projektant" };

export const norm = (s) => String(s || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

// Rozpracované „@dotaz" těsně před kurzorem (po začátku textu nebo mezeře).
export function activeQuery(text, caret) {
  const before = String(text || "").slice(0, caret);
  const m = before.match(/(^|\s)@([^\s@]{0,30})$/);
  return m ? { query: m[2], start: before.length - m[2].length - 1 } : null;
}

// Id označených, jejichž „@Jméno" v textu opravdu zůstalo (smazané se neposílá).
export function mentionIds(text, picked) {
  return Object.entries(picked || {}).filter(([, name]) => String(text || "").includes(`@${name}`)).map(([id]) => id);
}

export function MentionTextarea({ value, onChange, loadPeople, picked, onPick, ...rest }) {
  const ref = useRef(null);
  const [people, setPeople] = useState(null); // null = ještě nenačteno
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState(null);           // {query, start} | null
  const [active, setActive] = useState(0);

  const ensurePeople = () => {
    if (people !== null || loading) return;
    setLoading(true);
    Promise.resolve(loadPeople?.()).then((list) => setPeople(list || [])).catch(() => setPeople([]))
      .finally(() => setLoading(false));
  };

  const options = useMemo(() => {
    if (!q || !people) return [];
    const nq = norm(q.query);
    return people.filter((p) => !nq || norm(p.name).split(" ").some((w) => w.startsWith(nq)) || norm(p.name).includes(nq)).slice(0, 6);
  }, [q, people]);
  useEffect(() => { setActive(0); }, [q?.query]);

  const refresh = (text, caret) => {
    const aq = activeQuery(text, caret);
    if (aq) ensurePeople();
    setQ(aq);
  };

  const choose = (p) => {
    const el = ref.current;
    const caret = el ? el.selectionStart : value.length;
    const start = q ? q.start : caret;
    const insert = `@${p.name} `;
    const next = value.slice(0, start) + insert + value.slice(caret);
    onChange(next);
    onPick?.({ ...(picked || {}), [p.id]: p.name });
    setQ(null);
    requestAnimationFrame(() => {
      if (!el) return;
      const pos = start + insert.length;
      el.focus(); el.setSelectionRange(pos, pos);
    });
  };

  const onKeyDown = (e) => {
    if (!q || options.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => (a + 1) % options.length); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => (a - 1 + options.length) % options.length); }
    else if (e.key === "Enter" || e.key === "Tab") { e.preventDefault(); choose(options[active]); }
    else if (e.key === "Escape") { e.preventDefault(); setQ(null); }
  };

  return (
    <div className="relative flex-1 min-w-0">
      <Textarea ref={ref} value={value} {...rest}
        onChange={(e) => { onChange(e.target.value); refresh(e.target.value, e.target.selectionStart); }}
        onKeyDown={onKeyDown}
        onClick={(e) => refresh(e.target.value, e.target.selectionStart)}
        onBlur={() => setTimeout(() => setQ(null), 150)} />
      {q && (options.length > 0 || loading || people !== null) && (
        <div role="listbox" data-testid="mention-list"
          className="absolute left-0 right-0 top-full mt-1 z-50 rounded-[12px] border border-border bg-popover text-popover-foreground shadow-lg overflow-hidden">
          {options.length === 0 ? (
            <div className="px-3 py-2.5 text-sm text-muted-foreground">{loading ? "Načítám…" : "Nikdo neodpovídá."}</div>
          ) : options.map((p, i) => (
            <button key={p.id} type="button" role="option" aria-selected={i === active} data-testid={`mention-opt-${p.id}`}
              onMouseDown={(e) => { e.preventDefault(); choose(p); }}
              className={`w-full flex items-center gap-2.5 px-3 py-2 min-h-[44px] text-left ${i === active ? "bg-secondary" : "hover:bg-secondary/60"}`}>
              <Avatar name={p.name} photo={p.photo_url} size={26} className="bg-brand/15 text-brand shrink-0" />
              <span className="min-w-0 flex-1 truncate text-sm font-semibold">{p.name}</span>
              <span className="text-xs text-muted-foreground shrink-0">{ROLE[p.role] || ""}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Text komentáře se zvýrazněnými označenými jmény; zbytek přes Linkify (odkazy).
export function MentionText({ text, mentions }) {
  const names = [...new Set((mentions || []).map((m) => m.name).filter(Boolean))].sort((a, b) => b.length - a.length);
  if (!names.length) return <Linkify text={text} />;
  const esc = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const re = new RegExp(`(@(?:${names.map(esc).join("|")}))`, "g");
  return (
    <span>
      {String(text || "").split(re).map((part, i) => (i % 2 === 1
        ? <span key={i} className="font-bold text-brand" data-testid="mention-chip">{part}</span>
        : <Linkify key={i} text={part} />))}
    </span>
  );
}
